import { Document, Page, View } from "@react-pdf/renderer";
import { styles } from "../reportingHelpers";
import { IntlProvider } from "react-intl";
import {
  FixtureReportsHeader,
  parseScoringIntervals,
} from "./FixtureReportsHeader";
import { SideTableLeft } from "./SideTableLeft";
import { SideTableRight } from "./SideTableRight";
import { TeamBoxscoreTable } from "./TeamBoxscoreTable";
import { ScoringTable } from "./ScoringTable";
import { FixtureReportsFooter } from "../FixtureReportsFooter";

const FrenchBoxScoreReport = (props) => {
  const {
    homeTeam,
    awayTeam,
    fixture,
    scoringIntervals,
    roles,
    fixtureProfile: { profile: fixtureProfile },
  } = props.data;
  const { language, locale, intl } = props;

  let homeTeamPeriods,
    awayTeamPeriods = [];
  let periodsScores = [];

  if (homeTeam && awayTeam && scoringIntervals) {
    homeTeamPeriods = parseScoringIntervals(homeTeam, scoringIntervals, intl);
    awayTeamPeriods = parseScoringIntervals(awayTeam, scoringIntervals, intl);

    let lastHomeScore = 0,
      lastAwayScore = 0;
    homeTeamPeriods.forEach((period, index) => {
      const awayPeriod = awayTeamPeriods[index];
      const newHomeScore = period.interval2 || period.interval1;
      const newAwayScore = awayPeriod.interval2 || awayPeriod.interval1;
      periodsScores.push(
        `${newHomeScore - lastHomeScore}-${newAwayScore - lastAwayScore}`
      );
      lastHomeScore = newHomeScore;
      lastAwayScore = newAwayScore;
    });

    homeTeam["score"] = lastHomeScore;
    awayTeam["score"] = lastAwayScore;

    homeTeam?.personStats && homeTeam.personStats.sort((a, b) => a.bib - b.bib);
    awayTeam?.personStats && awayTeam.personStats.sort((a, b) => a.bib - b.bib);

    return (
      <IntlProvider locale={locale ?? "en-US"}>
        <Document>
          <Page
            size="A4"
            orientation="landscape"
            style={styles.page}
            wrap={false}
          >
            <BoxScoreReportPage
              {...{
                homeTeam,
                awayTeam,
                fixture,
                homeTeamPeriods,
                awayTeamPeriods,
                periodsScores,
                roles,
                language,
                locale,
                intl,
                fixtureProfile,
              }}
            />
          </Page>
        </Document>
      </IntlProvider>
    );
  } else {
    return null;
  }
};

const BoxScoreReportPage = ({
  homeTeam,
  awayTeam,
  fixture,
  roles,
  homeTeamPeriods,
  awayTeamPeriods,
  periodsScores,
  language,
  locale,
  intl,
  fixtureProfile,
}) => {
  const homeReboundsOffensive = homeTeam.teamStatsPeriods
    ? homeTeam.teamStatsPeriods.reduce(
        (acc, stats) => acc + stats.totals?.reboundsOffensive,
        0
      )
    : 0;
  const homeReboundsDefensive = homeTeam.teamStatsPeriods
    ? homeTeam.teamStatsPeriods.reduce(
        (acc, stats) => acc + stats.totals?.reboundsDefensive,
        0
      )
    : 0;
  const awayReboundsOffensive = awayTeam.teamStatsPeriods
    ? awayTeam.teamStatsPeriods.reduce(
        (acc, stats) => acc + stats.totals?.reboundsOffensive,
        0
      )
    : 0;
  const awayReboundsDefensive = awayTeam.teamStatsPeriods
    ? awayTeam.teamStatsPeriods.reduce(
        (acc, stats) => acc + stats.totals?.reboundsDefensive,
        0
      )
    : 0;

  const reboundsTotalInGame =
    homeTeam.teamStatsPeriods && awayTeam.teamStatsPeriods
      ? homeTeam.teamStatsPeriods.reduce(
          (acc, stats) => acc + stats.totals?.rebounds,
          0
        ) +
        awayTeam.teamStatsPeriods.reduce(
          (acc, stats) => acc + stats.totals?.rebounds,
          0
        )
      : 0;

  const homeOffensiveReboundsPercentage =
    /* off1/(off1+def2) */ (100 * homeReboundsOffensive) /
    (homeReboundsOffensive + awayReboundsDefensive);
  const homeDefensiveReboundsPercentage =
    /* def1/(def1+off2) */ (100 * homeReboundsDefensive) /
    (homeReboundsDefensive + awayReboundsOffensive);
  const awayOffensiveReboundsPercentage =
    (100 * awayReboundsOffensive) /
    (awayReboundsOffensive + homeReboundsDefensive);
  const awayDefensiveReboundsPercentage =
    (100 * awayReboundsDefensive) /
    (awayReboundsDefensive + homeReboundsOffensive);
  return (
    <View>
      <View
        style={{
          marginBottom: "2mm",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <FixtureReportsHeader
          {...{
            homeTeam,
            awayTeam,
            fixture,
            periodsScores,
            language,
            locale,
            roles,
            title: intl.formatMessage({
              id: "reports.boxscore",
              defaultMessage: "Boxscore",
            }),
            intl,
          }}
        />
        <ScoringTable
          fixture={fixture}
          homeTeam={homeTeam.team}
          awayTeam={awayTeam.team}
          {...{ homeTeamPeriods, awayTeamPeriods, intl, fixtureProfile }}
        />
      </View>

      <View
        style={{
          ...styles.section,
          flexDirection: "row",
          marginBottom: 0,
        }}
      >
        <View
          style={{
            ...styles.section,
            marginBottom: 0,
            borderBottomWidth: 1,
            paddingBottom: 1,
          }}
        >
          <TeamBoxscoreTable team={homeTeam} roles={roles} intl={intl} />
          <TeamBoxscoreTable team={awayTeam} roles={roles} intl={intl} />
        </View>
        <View
          style={{
            ...styles.section,
            border: 1,
            borderLeftWidth: 0,
            height: "100%",
          }}
        >
          <SideTableLeft
            team={homeTeam}
            offensiveReboundsPercentage={homeOffensiveReboundsPercentage}
            defensiveReboundsPercentage={homeDefensiveReboundsPercentage}
            {...{ intl, reboundsTotalInGame }}
          />
          <SideTableRight team={homeTeam} {...{ intl }} />
          <View
            style={{
              ...styles.tableRowFrench,
              height: "6mm",
            }}
          />
          <SideTableLeft
            team={awayTeam}
            offensiveReboundsPercentage={awayOffensiveReboundsPercentage}
            defensiveReboundsPercentage={awayDefensiveReboundsPercentage}
            {...{ intl, reboundsTotalInGame }}
          />
          <SideTableRight team={awayTeam} {...{ intl }} />
        </View>
      </View>
      <View
        style={{
          marginTop: 10,
        }}
      >
        <FixtureReportsFooter roles={roles} intl={intl} />
      </View>
    </View>
  );
};

export default FrenchBoxScoreReport;
